import { useState, useEffect, useCallback } from "react";
import { authFetch, API } from "@/App";
import { Search, CheckCircle, XCircle, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function QualityControlPage() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ batch_number: "", product: "", shift: "A", compressive_3d: "", compressive_7d: "", compressive_28d: "", fineness: "", setting_initial: "", setting_final: "" });

  const loadBatches = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/factory/qc-batches`);
      const json = await res.json();
      if (json.success) setBatches(json.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await authFetch(`${API}/factory/qc-batches`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          compressive_3d: parseFloat(form.compressive_3d), compressive_7d: parseFloat(form.compressive_7d),
          compressive_28d: parseFloat(form.compressive_28d), fineness: parseFloat(form.fineness),
          setting_initial: parseInt(form.setting_initial), setting_final: parseInt(form.setting_final),
        }),
      });
      const json = await res.json();
      if (json.success) {
        toast.success(`QC batch ${json.data.result === "PASS" ? "PASSED" : "FAILED"}`);
        setShowForm(false);
        setForm({ batch_number: "", product: "", shift: "A", compressive_3d: "", compressive_7d: "", compressive_28d: "", fineness: "", setting_initial: "", setting_final: "" });
        loadBatches();
      }
    } catch (e) { toast.error("Failed to add QC batch"); }
  };

  const filtered = batches.filter((b) => b.product?.toLowerCase().includes(search.toLowerCase()) || b.batch_number?.toLowerCase().includes(search.toLowerCase()));
  const passRate = batches.length > 0 ? ((batches.filter((b) => b.result === "PASS").length / batches.length) * 100).toFixed(1) : "100.0";

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="qc-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Quality Control</h1>
          <p className="text-slate-400 text-sm mt-1">Batch testing and compliance. Pass rate: <span className="text-emerald-400 font-medium">{passRate}%</span></p>
        </div>
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogTrigger asChild>
            <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700" data-testid="add-qc-batch">
              <Plus className="w-4 h-4" /> New Test
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#1C2B2B] border-emerald-900/30 text-white max-w-lg">
            <DialogHeader><DialogTitle>Add QC Batch Test</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-slate-400">Batch Number</label><input value={form.batch_number} onChange={(e) => setForm({ ...form, batch_number: e.target.value })} placeholder="B-XXXX" className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm" required /></div>
                <div><label className="text-xs text-slate-400">Shift</label><select value={form.shift} onChange={(e) => setForm({ ...form, shift: e.target.value })} className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm"><option value="A">A</option><option value="B">B</option><option value="C">C</option></select></div>
              </div>
              <div><label className="text-xs text-slate-400">Product</label><input value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm" required /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="text-xs text-slate-400">3d MPa</label><input type="number" step="0.1" value={form.compressive_3d} onChange={(e) => setForm({ ...form, compressive_3d: e.target.value })} className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm" required /></div>
                <div><label className="text-xs text-slate-400">7d MPa</label><input type="number" step="0.1" value={form.compressive_7d} onChange={(e) => setForm({ ...form, compressive_7d: e.target.value })} className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm" required /></div>
                <div><label className="text-xs text-slate-400">28d MPa</label><input type="number" step="0.1" value={form.compressive_28d} onChange={(e) => setForm({ ...form, compressive_28d: e.target.value })} className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm" required /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="text-xs text-slate-400">Fineness (m²/kg)</label><input type="number" value={form.fineness} onChange={(e) => setForm({ ...form, fineness: e.target.value })} className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm" required /></div>
                <div><label className="text-xs text-slate-400">Initial Set (min)</label><input type="number" value={form.setting_initial} onChange={(e) => setForm({ ...form, setting_initial: e.target.value })} className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm" required /></div>
                <div><label className="text-xs text-slate-400">Final Set (min)</label><input type="number" value={form.setting_final} onChange={(e) => setForm({ ...form, setting_final: e.target.value })} className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm" required /></div>
              </div>
              <button type="submit" className="w-full py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700" data-testid="submit-qc-batch">Submit Test Result</button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input type="text" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-[#1C2B2B] border border-emerald-900/30 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-emerald-600" data-testid="qc-search" />
      </div>

      <div className="bg-[#1C2B2B] border border-emerald-900/30 rounded-xl overflow-x-auto">
        <table className="w-full" data-testid="qc-table">
          <thead>
            <tr className="border-b border-emerald-900/30">
              <th className="text-left px-4 py-3 text-xs text-slate-400 uppercase whitespace-nowrap">Batch</th>
              <th className="text-left px-4 py-3 text-xs text-slate-400 uppercase whitespace-nowrap">Product</th>
              <th className="text-left px-4 py-3 text-xs text-slate-400 uppercase whitespace-nowrap">Date</th>
              <th className="text-right px-4 py-3 text-xs text-slate-400 uppercase whitespace-nowrap">3d MPa</th>
              <th className="text-right px-4 py-3 text-xs text-slate-400 uppercase whitespace-nowrap">7d MPa</th>
              <th className="text-right px-4 py-3 text-xs text-slate-400 uppercase whitespace-nowrap">28d MPa</th>
              <th className="text-right px-4 py-3 text-xs text-slate-400 uppercase whitespace-nowrap">Fineness</th>
              <th className="text-center px-4 py-3 text-xs text-slate-400 uppercase whitespace-nowrap">Result</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan="8" className="text-center py-8 text-slate-500">No QC tests yet. Add your first test.</td></tr>}
            {filtered.map((b) => (
              <tr key={b.batch_id} className="border-b border-emerald-900/20 hover:bg-white/[0.02]">
                <td className="px-4 py-3 text-sm text-white font-mono">{b.batch_number}</td>
                <td className="px-4 py-3 text-sm text-white">{b.product}</td>
                <td className="px-4 py-3 text-sm text-slate-300">{b.date}</td>
                <td className="px-4 py-3 text-sm text-slate-300 text-right">{b.compressive_3d}</td>
                <td className="px-4 py-3 text-sm text-slate-300 text-right">{b.compressive_7d}</td>
                <td className="px-4 py-3 text-sm text-slate-300 text-right">{b.compressive_28d}</td>
                <td className="px-4 py-3 text-sm text-slate-300 text-right">{b.fineness} m²/kg</td>
                <td className="px-4 py-3 text-center">
                  {b.result === "PASS" ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-600/20 text-emerald-400 text-xs font-medium"><CheckCircle className="w-3 h-3" /> PASS</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-600/20 text-red-400 text-xs font-medium"><XCircle className="w-3 h-3" /> FAIL</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
